import { FC, useEffect, useState } from "react";

import { SchemaDetails } from "./slices/SchemaDetails";
import { AdditionalInformation } from "./slices/AdditionalInformation";
import {
  ApplicantSettingsErrors,
  DocumentTimeframe,
  Template,
  VerificationSchema,
  VerificationStep
} from "types/landlord/verificationTemplate";
import { DecisionCriteria } from "./slices/DecisionCriteria";
import { ApplicantType, VerificationNames } from "shared/types";
import { GeneralSettingsView } from "./slices/GeneralSettingsView";
import { LeadSourceView } from "./slices/LeadSourceView";
import {
  buildVerificationSchemaData,
  getValueByPath,
  transformIncomeVerification
} from "./utils";
import { VerificationsSlice } from "./slices/VerificationsSlice";
import { DisclosureSlice } from "./slices/DisclosureSlice";
import { VerificationSchemaView } from "./slices/types";
import { TemplateContext } from "./provider";
import { Show } from "shared/components";
import { Layout, Props as LayoutProps } from "../../../providers/Layout";
import { IdentityVerificationSlice } from "./slices/IdentityVerificationSlice";
import { CriminalReportSlice } from "./slices/CriminalReportSlice";
import { cx } from "../../../utils/classnames";
import {
  basicInformationTabs,
  housingHistoryFallback,
  housingHistoryTabs,
  incomeHistoryTabs,
  screeningServicesTabs
} from "./constant";
import { CoSignerDisabledSlice } from "./slices/CoSignerDisabledSlice";
import { DocumentVerificationSlice } from "./slices/DocumentVerificationSlice";
import { BankVerificationSlice } from "./slices/BankVerificaitonSlice";
import { PayrollVerificationSlice } from "./slices/PayrollVerificationSlice";
import { HousingHistoryVerificationSlice } from "./slices/HousingHistoryVerificationSlice";
import { CreditReportSlice } from "./slices/CreditReportSlice";

export const VerificationTemplate: FC<{
  verificationTemplate: Template;
  savedTemplate: Template;
  verificationSchemas: VerificationSchema[];
  onTemplateUpdate?: (template: Template) => void;
  readOnly?: boolean;
  createMode?: boolean;
  isModal?: boolean;
  originalQualificationsDisclosureText: string;
  originalSubmissionDisclosureText: string;
  applicantSettingsErrors?: ApplicantSettingsErrors | null;
  onApplicantSettingsErrorsUpdate?: (
    errors: ApplicantSettingsErrors | null
  ) => void;
  layoutProps?: Omit<LayoutProps, "children">;
}> = ({
  verificationTemplate,
  savedTemplate,
  verificationSchemas,
  onTemplateUpdate: _onTemplateUpdate = () => undefined,
  readOnly = false,
  isModal,
  createMode,
  originalQualificationsDisclosureText,
  originalSubmissionDisclosureText,
  applicantSettingsErrors,
  onApplicantSettingsErrorsUpdate,
  layoutProps
}) => {
  const [partnerType, setPartnerType] = useState<ApplicantType>(
    ApplicantType.Applicant
  );

  const [selectedVerification, setSelectedVerification] =
    useState<VerificationSchemaView>(VerificationSchemaView.ScreeningServices);
  const [selectedStep, setSelectedStep] = useState(0);
  const [verifiedIncome, setVerifiedIncome] = useState(
    Boolean(
      verificationTemplate.verification_schemas[partnerType]
        .verified_income_verification
    )
      ? verificationTemplate.verification_schemas[partnerType]
          .verified_income_verification
      : {
          allow_bank: true,
          allow_payroll: true,
          allow_document: true,
          document_timeframe: {
            bank_statements: { value: 3, unit: "month" },
            paystubs: { value: 1, unit: "month" },
            tax_returns: { value: 3, unit: "year" },
            w2: { value: 1, unit: "year" }
          } as DocumentTimeframe
        }
  );

  const isIncomeHistoryTabs = incomeHistoryTabs.includes(selectedVerification);
  const isRentalApplicationTabs =
    basicInformationTabs.includes(selectedVerification);
  const isHousingHistoryTabs =
    housingHistoryTabs.includes(selectedVerification);

  const selectedVerificationSchema = isRentalApplicationTabs
    ? VerificationNames.RentalApplication
    : isIncomeHistoryTabs
      ? VerificationNames.IncomeVerification
      : isHousingHistoryTabs
        ? VerificationNames.HousingHistory
        : selectedVerification;

  const baseSchema = verificationSchemas.find(
    (schema: VerificationSchema) => schema.name === selectedVerificationSchema
  );

  const combinedVerificationSchemas =
    selectedVerificationSchema === VerificationNames.IncomeVerification
      ? transformIncomeVerification(baseSchema)
      : baseSchema?.steps || [];

  const getVerificationSchemaSteps = (
    schemaName: string
  ): VerificationStep[] => {
    return (
      verificationSchemas.find((schema) => schema.name === schemaName)?.steps ||
      []
    );
  };

  const getHousingHistoryConfiguration = (
    verificationSteps: VerificationStep[]
  ): any => {
    if (
      !verificationSteps[0]?.json_schema?.properties?.address?.items
        ?.dependencies?.housing_type?.oneOf
    )
      return undefined;

    const oneOfArray =
      verificationSteps[0].json_schema.properties.address.items.dependencies
        .housing_type.oneOf;

    return oneOfArray.find((el: any) => el?.properties?.[selectedVerification])
      ?.properties[selectedVerification];
  };

  const verificationSchema = getVerificationSchemaSteps(
    selectedVerificationSchema
  );
  const currentHousingHistoryConfiguration =
    getHousingHistoryConfiguration(verificationSchema);

  const [template, setTemplate] = useState<Template>(() => {
    return {
      ...verificationTemplate,
      applicant_paid: verificationTemplate["applicant_fee"].cents > 0,
      co_signer_paid: verificationTemplate["co_signer_fee"].cents > 0
    };
  });

  const updateTemplate = (template: Template) => {
    template.name = verificationTemplate.name;
    setTemplate(template);
    _onTemplateUpdate(template);
  };

  const updateVerificationSchema = (keys: string[], value: boolean) => {
    const updatedTemplate = buildVerificationSchemaData(template, keys, value);

    updateTemplate(updatedTemplate);
  };

  const toggleServiceEnabled = (state?: boolean) => {
    const stepSchema = combinedVerificationSchemas[selectedStep];
    const serviceName = `${stepSchema.name}${isIncomeHistoryTabs ? "" : "_step"}_enabled`;
    const keys = [
      "verification_schemas",
      partnerType,
      selectedVerificationSchema,
      serviceName
    ];
    const enabledState = state ?? !getValueByPath<boolean>(template, keys);
    updateVerificationSchema(keys, enabledState);
  };

  const toggleServiceRequired = () => {
    const stepSchema = combinedVerificationSchemas[selectedStep];
    const serviceName = `${stepSchema.name}${isIncomeHistoryTabs ? "" : "_step"}_required`;
    const keys = [
      "verification_schemas",
      partnerType,
      selectedVerificationSchema,
      serviceName
    ];
    const enabledState = !getValueByPath<boolean>(template, keys);

    updateVerificationSchema(keys, enabledState);
  };

  const toggleItemServiceRequired = (keys: string[]) => {
    const enabledState = !getValueByPath<boolean>(template, keys);
    updateVerificationSchema(keys, enabledState);
  };

  const toggleAdditionalQuestions = () => {
    const serviceName = "additional_questions_step_enabled";
    const keys = [
      "verification_schemas",
      partnerType,
      VerificationNames.RentalApplication,
      serviceName
    ];
    const enabledState = !getValueByPath<boolean>(template, keys);
    updateVerificationSchema(keys, enabledState);
  };

  useEffect(() => {
    const generateSettings = (partnerType: ApplicantType) => ({
      ...verificationTemplate.settings[partnerType],
      identity_verification:
        "identity_verification" in verificationTemplate.settings[partnerType]
          ? {
              ...verificationTemplate.settings[partnerType]
                .identity_verification,
              selfie_check: Boolean(
                verificationTemplate.settings[partnerType].identity_verification
                  ?.selfie_check
              ),
              prevent_apply_if_failed:
                verificationTemplate.settings[partnerType].identity_verification
                  ?.prevent_apply_if_failed ?? false
            }
          : {
              selfie_check: false,
              prevent_apply_if_failed: true
            }
    });

    const generateVerificationSchemas = (partnerType: ApplicantType) => ({
      ...verificationTemplate.verification_schemas[partnerType],
      housing_history: !verificationTemplate.verification_schemas[partnerType]
        .housing_history
        ? {
            ...housingHistoryFallback,
            address_step_enabled: false
          }
        : !(
              "address_step_enabled" in
              (verificationTemplate.verification_schemas[partnerType]
                .housing_history || {})
            )
          ? {
              ...housingHistoryFallback,
              address_step_enabled: true,
              ...verificationTemplate.verification_schemas[partnerType]
                .housing_history
            }
          : // This may cause some test templates -- not having a
            // Configuration for the housing history.
            // But it's too rare of occasion to be concerned about.
            // Only a few staging templates are affected.
            verificationTemplate.verification_schemas[partnerType]
              .housing_history
    });

    updateTemplate({
      ...verificationTemplate,
      applicant_paid: Boolean(template["applicant_paid"]),
      co_signer_paid: Boolean(template["co_signer_paid"]),
      settings: {
        ...verificationTemplate.settings,
        applicant: generateSettings(ApplicantType.Applicant),
        co_signer: generateSettings(ApplicantType.CoSigner)
      },
      verification_schemas: {
        applicant: generateVerificationSchemas(ApplicantType.Applicant),
        co_signer: generateVerificationSchemas(ApplicantType.CoSigner)
      }
    });
  }, []);

  return (
    <TemplateContext.Provider
      value={{
        partnerType,
        setPartnerType,
        selectedVerificationSchema: selectedVerification,
        setSelectedVerificationSchema: setSelectedVerification,
        combinedVerificationSchemas,
        selectedStep,
        setSelectedStep,
        template,
        savedTemplate: savedTemplate,
        verificationSchemas,
        setTemplate: updateTemplate,
        readOnly: Boolean(readOnly),
        isModal: Boolean(isModal),
        createMode: Boolean(createMode),
        verifiedIncome: verifiedIncome || {},
        setVerifiedIncome
      }}
    >
      <Layout
        hideHeaderOnScroll
        layoutClassName={isModal ? "!h-full" : undefined}
        footerClassName="bg-background-1000"
        headerClassName="w-full"
        sectionClassName="w-full"
        {...layoutProps}
      >
        <div
          className={cx(
            "mx-auto md:py-8 h-full",
            screeningServicesTabs.includes(selectedVerification)
              ? "max-w-[768px]"
              : [
                    VerificationSchemaView.DecisionCriteria,
                    VerificationSchemaView.TenantQualifications,
                    VerificationSchemaView.SubmissionDisclosure,
                    VerificationSchemaView.General
                  ].includes(selectedVerification)
                ? "max-w-[892px]"
                : "max-w-[994px]"
          )}
        >
          <Show
            if={
              selectedVerification === VerificationSchemaView.DecisionCriteria
            }
          >
            <DecisionCriteria
              applicantSettingsErrors={applicantSettingsErrors}
              onApplicantSettingsErrorsUpdate={onApplicantSettingsErrorsUpdate}
            />
          </Show>

          <Show
            if={
              selectedVerification === VerificationSchemaView.ScreeningServices
            }
          >
            {partnerType === ApplicantType.CoSigner &&
            !template.settings.co_signer.enabled_screening_services ? (
              <CoSignerDisabledSlice />
            ) : (
              <VerificationsSlice verificationSchemas={verificationSchemas} />
            )}
          </Show>

          <Show if={selectedVerification === VerificationSchemaView.General}>
            <GeneralSettingsView />
          </Show>

          <Show if={selectedVerification === VerificationSchemaView.LeadSource}>
            <LeadSourceView
              toggleServiceEnabled={toggleServiceEnabled}
              toggleServiceRequired={toggleServiceRequired}
            />
          </Show>

          <Show
            if={
              selectedVerification ===
              VerificationSchemaView.AdditionalQuestions
            }
          >
            <AdditionalInformation
              toggleServiceEnabled={toggleAdditionalQuestions}
            />
          </Show>

          <Show
            if={[
              VerificationNames.SubmissionDisclosure,
              VerificationNames.TermsAndConditions
            ].includes(selectedVerification as unknown as VerificationNames)}
          >
            <DisclosureSlice
              key={selectedVerification}
              verificationName={
                selectedVerification as unknown as
                  | VerificationNames.SubmissionDisclosure
                  | VerificationNames.TermsAndConditions
              }
              original={
                String(selectedVerification) ===
                VerificationNames.TermsAndConditions
                  ? originalQualificationsDisclosureText
                  : originalSubmissionDisclosureText
              }
            />
          </Show>

          <Show
            if={[
              VerificationSchemaView.RentVerification,
              VerificationSchemaView.OwnVerification,
              VerificationSchemaView.OtherVerification
            ].includes(selectedVerification)}
          >
            <HousingHistoryVerificationSlice
              details={currentHousingHistoryConfiguration}
              toggleItemServiceRequired={toggleItemServiceRequired}
            />
          </Show>

          {/* TODO: fixme */}
          <Show
            if={[
              VerificationSchemaView.MilitaryOrTeacher,
              VerificationSchemaView.PersonalDetails,
              VerificationSchemaView.Dependents,
              VerificationSchemaView.EmergencyContacts,
              VerificationSchemaView.Vehicles,
              VerificationSchemaView.MilitaryFirstResponderTeacher,
              VerificationSchemaView.Pets,
              VerificationSchemaView.Employment,
              VerificationSchemaView.SelfEmployed,
              VerificationSchemaView.OfferLetter,
              VerificationSchemaView.OtherIncome
            ].includes(selectedVerification)}
          >
            <SchemaDetails
              toggleServiceEnabled={toggleServiceEnabled}
              toggleItemServiceRequired={toggleItemServiceRequired}
            />
          </Show>

          {[
            {
              view: VerificationSchemaView.IdentityVerification,
              slice: <IdentityVerificationSlice />
            },
            {
              view: VerificationSchemaView.CriminalReport,
              slice: <CriminalReportSlice />
            },
            {
              view: VerificationSchemaView.CreditReport,
              slice: <CreditReportSlice />
            },
            {
              view: VerificationSchemaView.DocumentVerification,
              slice: <DocumentVerificationSlice />
            },
            {
              view: VerificationSchemaView.BankVerification,
              slice: <BankVerificationSlice />
            },
            {
              view: VerificationSchemaView.PayrollVerification,
              slice: <PayrollVerificationSlice />
            }
          ].map(({ view, slice }) => (
            <Show key={view} if={selectedVerification === view}>
              {slice}
            </Show>
          ))}
        </div>
      </Layout>
    </TemplateContext.Provider>
  );
};
